import { useRef, useState } from "react";
import api from "@/lib/api";
import { speakText, stopSpeaking } from "@/lib/voice";
import { PlayCircle, Square, Loader2, RotateCcw } from "lucide-react";

// Audio mode: auto-play an entire track (all courses, all lessons) back-to-back using the
// cinematic voice (ElevenLabs when in quota, else the free Piper narrator). Remembers where a
// member stopped (per track, in localStorage) and offers to resume from that spot.
export function TrackAudioPlayer({ track, className = "" }) {
  const posKey = `track_audio_pos_${track.id}`;
  const [state, setState] = useState("idle"); // idle | loading | playing
  const [now, setNow] = useState("");
  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(posKey)); } catch { return null; }
  });
  const cancelRef = useRef(false);

  const savePos = (ci, li, label) => {
    const p = { ci, li, label };
    localStorage.setItem(posKey, JSON.stringify(p));
    setSaved(p);
  };
  const clearPos = () => { localStorage.removeItem(posKey); setSaved(null); };

  const stop = () => { cancelRef.current = true; stopSpeaking(); setState("idle"); setNow(""); };

  const run = async (startCi = 0, startLi = 0) => {
    cancelRef.current = false;
    setState("loading");
    const voiceId = localStorage.getItem("oracle_voice_id") || undefined;
    const speed = parseFloat(localStorage.getItem("oracle_speed")) || 1.0;
    const say = (t) => speakText(t, { voiceId, speed, isCanceled: () => cancelRef.current });
    const courses = track.courses || [];
    if (startCi >= courses.length) { startCi = 0; startLi = 0; }
    try {
      setState("playing");
      if (startCi === 0 && startLi === 0) {
        savePos(0, 0, `${track.name} — Introduction`);
        setNow("Introduction");
        await say(`${track.name}. ${track.blurb || ""}`);
      }
      for (let ci = startCi; ci < courses.length; ci++) {
        if (cancelRef.current) return;
        const c = courses[ci];
        setNow(c.title);
        let course = c;
        try { const r = await api.get(`/courses/${c.slug}`); course = r.data; } catch { /* keep summary */ }
        const lessons = course.lessons || [];
        const li0 = ci === startCi ? startLi : 0;
        if (li0 === 0) {
          savePos(ci, 0, c.title);
          await say(`${course.title}. ${course.subtitle || ""}. ${course.summary || ""}`);
        }
        for (let li = li0; li < lessons.length; li++) {
          if (cancelRef.current) return;
          savePos(ci, li, `${c.title} — ${lessons[li].title}`);
          setNow(`${c.title}: ${lessons[li].title}`);
          await say(`${lessons[li].title}. ${lessons[li].body}`);
        }
      }
      clearPos(); // reached the end
    } finally {
      if (!cancelRef.current) { setState("idle"); setNow(""); }
    }
  };

  const busy = state === "playing" || state === "loading";
  const btnBase = "inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-sm border transition-colors";

  if (busy) {
    return (
      <button onClick={stop} data-testid={`track-audio-${track.id}`}
        className={`${btnBase} border-primary text-primary bg-primary/10 ${className}`} title="Stop audio">
        {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
        {state === "loading" ? "Loading…" : now ? `Playing: ${now}` : "Playing…"}
      </button>
    );
  }

  if (saved) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <button onClick={() => run(saved.ci, saved.li)} data-testid={`track-audio-${track.id}`}
          className={`${btnBase} border-primary text-primary bg-primary/10 hover:bg-primary/20`}
          title={saved.label ? `Resume: ${saved.label}` : "Resume this track"}>
          <PlayCircle className="w-4 h-4" /> Resume track
        </button>
        <button onClick={() => run(0, 0)} data-testid={`track-audio-restart-${track.id}`}
          className={`${btnBase} border-border text-muted-foreground hover:border-primary hover:text-primary`}
          title="Start this track from the beginning">
          <RotateCcw className="w-4 h-4" /> Start over
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => run(0, 0)} data-testid={`track-audio-${track.id}`}
      className={`${btnBase} border-border text-muted-foreground hover:border-primary hover:text-primary ${className}`}
      title="Play this whole track as audio">
      <PlayCircle className="w-4 h-4" /> Play track
    </button>
  );
}

export default TrackAudioPlayer;
